import { useTranslation } from 'react-i18next';
import { supplierById } from '@d-orca/shared/client/logistics';
import type { Delivery, DeliveryBoardStatus, Supplier } from '@interfaces/ILogistics';
import { displayStatusName, overdueDays } from '../board.logic';
import { CriticalStar } from '../panels/CriticalStar';
import { cargoLabel } from './calendar.labels';
import { orderTone, toneStyle } from '../deliveryTone';
import styles from './CalendarOrderCard.module.css';

interface ICalendarOrderCardProps {
  delivery: Delivery;
  statuses: DeliveryBoardStatus[];
  suppliers: Supplier[];
  tz: string;
  /** The site's day, which decides lateness. */
  today: string;
  onOpen: (delivery: Delivery) => void;
}

/** One order in the side column; opens the order's drawer. */
export function CalendarOrderCard({ delivery, statuses, suppliers, tz, today, onOpen }: ICalendarOrderCardProps) {
  const { t } = useTranslation();
  const status = statuses.find((candidate) => candidate.id === delivery.status);
  const statusName = delivery.isSynced
    ? t('logistics.calendar.synced')
    : status && displayStatusName(status, t);
  // A site can delete a supplier or a column an order still names; the fact just drops out.
  const facts = [supplierById(suppliers, delivery.supplierId)?.name, statusName].filter(Boolean);
  const daysLate = overdueDays(delivery, new Date(), tz);

  return (
    <button type="button" className={styles.card} style={toneStyle(orderTone(delivery, statuses, today))} onClick={() => onOpen(delivery)}>
      <span className={styles.title}>
        <span>#{delivery.ref} · {delivery.destinationName}</span>
        {delivery.isCritical ? <CriticalStar critical /> : null}
      </span>
      <span className={styles.cargo}>{cargoLabel(delivery, t)}</span>
      <span className={styles.facts}>
        <span>{facts.join(' · ')}</span>
        {daysLate > 0 ? <span className={styles.late}>{t('logistics.board.overdueBadge', { n: daysLate })}</span> : null}
      </span>
    </button>
  );
}
